// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useMemo } from 'react';

import type { VDCOSChartToolType } from '@evolvemep/api-ts';

import { FiltersPopover } from 'components/Mantine/GlobalFilters/FiltersPopover';
import { TimeFrameRequired } from 'components/Mantine/GlobalFilters/TimeFrameRequired';
import { WrappedMultiSelect } from 'components/Mantine/TypeSafeSelect/WrappedMultiSelect';
import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';
import {
  type VDCOSChartOptionalFilters,
  chartToolTypeLabels,
  type VDCOSChartFilters,
  useChartFilters,
} from 'modules/Shop/Insights/RevitUsage/useChartFilters';
import { useFilterData } from 'modules/Shop/Insights/RevitUsage/useFilterData';

export type AnalyticsFiltersProps = {
  usedFilters?: (keyof VDCOSChartFilters)[];
  pendingFilters: VDCOSChartOptionalFilters;
  setFilters: (newFilters: Partial<VDCOSChartOptionalFilters>) => void;
  applyChanges: () => void;
  discardChanges: () => void;
  setToolType: (toolType: VDCOSChartToolType) => void;
  hasPendingChanges: boolean;
  filtersAreSet: boolean;
};

export const AnalyticsFilters = ({
  setToolType,
  pendingFilters,
  setFilters,
  applyChanges,
  discardChanges,
  hasPendingChanges,
  filtersAreSet,
  usedFilters,
}: AnalyticsFiltersProps) => {
  const { timeFrame, toolType } = useChartFilters();

  const chartToolTypeOptions = useMemo(
    () =>
      [
        { value: 'Evolve', label: chartToolTypeLabels['Evolve'] },
        { value: 'Revit', label: chartToolTypeLabels['Revit'] },
      ] as const satisfies { value: VDCOSChartToolType; label: string }[],
    [],
  );

  const { revitYears, loadingRevitYears, viableFeatureTypeNames, loadingFeatureTypes, viableUsers, loadingUsers } =
    useFilterData(
      {
        timeFrame,
        toolType,
        ...pendingFilters,
      },
      setFilters,
    );

  return (
    <>
      <TimeFrameRequired />
      <WrappedSelect
        value={toolType}
        onChange={(tt) => {
          if (tt) setToolType(tt);
        }}
        data={chartToolTypeOptions}
        withinPortal={false}
        comboboxProps={{
          withinPortal: false,
        }}
      />

      <FiltersPopover
        filtersAreSet={filtersAreSet}
        hasChanges={hasPendingChanges}
        onApplyChanges={applyChanges}
        onDiscardChanges={discardChanges}
      >
        {usedFilters?.includes('selectedRevitYear') && (
          <WrappedSelect
            label="Revit Version"
            value={pendingFilters.selectedRevitYear}
            onChange={(selectedRevitYear) => setFilters({ selectedRevitYear })}
            data={revitYears.map((value) => ({
              label: `Revit ${value}`,
              value,
            }))}
            disabled={revitYears.length === 0}
            loading={loadingRevitYears}
            placeholder="No versions found"
            withinPortal={false}
            comboboxProps={{
              withinPortal: false,
            }}
          />
        )}
        {usedFilters?.includes('userIds') && (
          <WrappedMultiSelect
            label="Users"
            value={pendingFilters.userIds}
            onChange={(userIds) =>
              setFilters({
                userIds,
              })
            }
            data={
              viableUsers?.map((user) => ({
                label: user.email,
                value: user.userId,
              })) ?? []
            }
            loading={loadingUsers}
            placeholder="All Users"
            autoDropdownWidth
            clearable
            searchable
            withinPortal={false}
            comboboxProps={{
              withinPortal: false,
            }}
          />
        )}
        {usedFilters?.includes('featureTypeNames') && (
          <WrappedMultiSelect
            label="Features"
            value={pendingFilters.featureTypeNames}
            onChange={(featureTypeNames) =>
              setFilters({
                featureTypeNames,
              })
            }
            data={viableFeatureTypeNames}
            loading={loadingFeatureTypes}
            placeholder="All Features"
            autoDropdownWidth
            clearable
            searchable
            withinPortal={false}
            comboboxProps={{
              withinPortal: false,
            }}
          />
        )}
      </FiltersPopover>
    </>
  );
};
